import ably from '@/helpers/ably';
import isLocalStorageAvailable from '@/helpers/isLocalStorageAvailable.js';

const state = {
    clientId: null,
    channel: null,
    members: [],
    isLeader: false,
    liveCart: null,
    clients: [],
    isAttaching: false,
    presenceListeners: null
};

const isChannelAttached = channel => channel?.state === 'attached';

const mutations = {
    SET_CHANNEL(state, channel) {
        state.channel = channel;
    },
    SET_CLIENT_ID(state, clientId) {
        state.clientId = clientId;
    },
    SET_IS_LEADER(state, isLeader) {
        state.isLeader = isLeader;
    },
    SET_MEMBERS(state, members) {
        state.members = [...members].sort(
            (a, b) => a.data.joinedAt - b.data.joinedAt
        );
    },
    SET_LIVE_CART(state, cart) {
        state.liveCart = cart;
    },
    SET_CLIENTS(state, clients) {
        state.clients = clients;
    },
    SET_IS_ATTACHING(state, isAttaching) {
        state.isAttaching = isAttaching;
    },
    SET_PRESENCE_LISTENERS(state, listeners) {
        state.presenceListeners = listeners;
    }
};

const getters = {
    otherClientsCount: state =>
        [
            ...new Set(
                state.members
                    ?.map(m => m.clientId)
                    .filter(id => id && id !== state.clientId)
            )
        ].length || 0,
    clients: state => state.clients,
    tableClientId: state => state.clientId,
    liveCart: state => state.liveCart,
    leaderId: state => state.members?.[0]?.clientId,
    isSuccessor: state => {
        const successorId = state.members[1]?.clientId || null;

        return state.clientId === successorId;
    },
    members: state => state.members
};

const actions = {
    async attach({ commit, dispatch, state, rootState, rootGetters }) {
        const {
            venue: {
                venue: { id: venueId }
            },
            cart: { selectedTableId: tableId }
        } = rootState;

        if (!tableId || !venueId) {
            return;
        }

        if (state.isAttaching) {
            return;
        }

        commit('SET_IS_ATTACHING', true);

        const channelName = `venue-${venueId}-table-${tableId}`;

        try {
            if (state.channel && state.channel.name !== channelName) {
                await dispatch('detach', { clearClientId: false });
            }

            const clientId = ably.auth.clientId;
            const { firstName, lastName } = rootState.customer;
            const customerName = firstName
                ? `${firstName} ${lastName ? lastName[0] + '.' : ''}`
                : `Customer-${clientId.split('-')[2]?.toString().slice(-2)}`;

            const channel = ably.channels.get(channelName, {
                modes: [
                    'PRESENCE',
                    'PRESENCE_SUBSCRIBE',
                    'OBJECT_SUBSCRIBE',
                    'OBJECT_PUBLISH'
                ]
            });

            await channel.attach();

            commit('SET_CHANNEL', channel);
            commit('SET_CLIENT_ID', clientId);

            const root = await channel.objects.getRoot();
            let map = root.get('liveCartMap');

            if (!map) {
                map = await channel.objects.createMap();

                await root.set('liveCartMap', map);
            }

            await map.set(`${clientId}`, rootGetters['cart/items']);

            map.subscribe(async update => {
                if (update.clientId === clientId) {
                    return;
                }

                commit('SET_CLIENTS', Object.fromEntries(map.entries()));
            });

            commit('SET_LIVE_CART', map);

            await channel.presence.enter({
                joinedAt: Date.now(),
                name: customerName
            });

            const onLeave = async member => {
                await dispatch('evaluateLeader');
                await dispatch('onMemberLeave', member);
            };
            const onEnter = async () => await dispatch('evaluateLeader');

            channel.presence.subscribe('leave', onLeave);
            channel.presence.subscribe('enter', onEnter);

            commit('SET_PRESENCE_LISTENERS', { onLeave, onEnter });
            commit('SET_CLIENTS', Object.fromEntries(map.entries()));

            await dispatch('evaluateLeader');

            if (state.isLeader) {
                await dispatch('purge');
            }
        } catch (err) {
            console.error(err);
        } finally {
            commit('SET_IS_ATTACHING', false);
        }
    },

    async detach({ state, commit }, { clearClientId = false }) {
        if (!state.liveCart || !state.channel) {
            return;
        }

        try {
            if (state.presenceListeners) {
                const { onLeave, onEnter } = state.presenceListeners;
                state.channel.presence.unsubscribe('leave', onLeave);
                state.channel.presence.unsubscribe('enter', onEnter);
                commit('SET_PRESENCE_LISTENERS', null);
            }

            await state.liveCart?.unsubscribeAll?.();
            await state.channel.detach();

            commit('SET_IS_LEADER', false);
            commit('SET_LIVE_CART', null);
            commit('SET_CHANNEL', null);
            commit('SET_MEMBERS', []);
            commit('SET_CLIENTS', []);

            if (clearClientId) {
                commit('SET_CLIENT_ID', null);
                isLocalStorageAvailable() &&
                    localStorage.removeItem('ablyClientId');
            }
        } catch (err) {
            console.error(err);
        }
    },

    async onMemberLeave({ state }, { clientId: leftId = '' }) {
        if (!state.isLeader || !state.liveCart) {
            return;
        }

        if (!isChannelAttached(state.channel)) {
            return;
        }

        try {
            await state.liveCart.remove(leftId);
        } catch (err) {
            console.error(err);
        }
    },

    async evaluateLeader({ state, commit }) {
        try {
            const members = await state.channel.presence.get();
            const sorted = members.sort(
                (a, b) => a.data.joinedAt - b.data.joinedAt
            );
            const leaderId = sorted[0]?.clientId;
            const isLeader = leaderId === state.clientId;

            commit('SET_MEMBERS', members);
            commit('SET_IS_LEADER', isLeader);
        } catch (err) {
            console.error(err);
        }
    },

    async purge({ state }) {
        if (!state.liveCart) {
            return;
        }

        if (!isChannelAttached(state.channel)) {
            return;
        }

        const connectedMemberIds = state.members.map(
            ({ clientId }) => clientId
        );

        const disconnectedClientIds = Object.keys(state.clients).filter(
            key => !connectedMemberIds.includes(key)
        );

        for (const id of disconnectedClientIds) {
            if (!isChannelAttached(state.channel)) {
                break;
            }

            try {
                await state.liveCart.remove(id);
            } catch (err) {
                console.error(err);
            }
        }
    },

    async updateLiveCart({ state, rootGetters, dispatch }) {
        const items = rootGetters['cart/items'];

        try {
            if (!state.liveCart || !state.channel) {
                await dispatch('attach');
            }

            if (!state.liveCart || !isChannelAttached(state.channel)) {
                return;
            }

            await state.liveCart.set(state.clientId, items);
        } catch (err) {
            console.error(err);
        }
    }
};

export default { namespaced: true, state, mutations, actions, getters };
